import React, { useState, useEffect, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { accountsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import AccountMatchingModal, {
  AccountMapping,
  PlaidTransactionImportMode,
} from './AccountMatchingModal';
import { Button } from './ui/button';
import InstitutionLogo from './InstitutionLogo';
import { useAuth } from '../contexts/AuthContext';

interface LinkedAccount {
  id: number;
  plaid_account_id: string;
  account_name: string;
  account_type: string;
  account_subtype: string;
  mask: string;
  institution_name: string;
  current_balance: number;
  last_sync_at: string;
  plaid_connection_id: number;
  connection_name: string;
  logo_url?: string;
  manual_account_id?: number;
  manual_account_name?: string;
  manual_account_institution?: string;
  manual_account_category?: string;
  needs_reauth?: boolean;
}

const BankConnections: React.FC = () => {
  const { user } = useAuth();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [connectingBank, setConnectingBank] = useState(false);
  const [plaidError, setPlaidError] = useState<string | null>(null);
  const plaidCompletedRef = useRef(false);

  // Re-auth state — tracks which connection is being re-authenticated
  const [reauthConnectionId, setReauthConnectionId] = useState<number | null>(null);

  const [pendingConnectionId, setPendingConnectionId] = useState<number | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [showAccountMatching, setShowAccountMatching] = useState(false);
  const [accountMatchingSuggestions, setAccountMatchingSuggestions] = useState<any[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [hasBusinessAccounts, setHasBusinessAccounts] = useState(false);
  const [plaidHistoryNote, setPlaidHistoryNote] = useState<string | null>(null);
  const [latestTransactionByManualAccount, setLatestTransactionByManualAccount] = useState<
    Record<string, string | null>
  >({});
  const [importSuccessMessage, setImportSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchLinkedAccounts();
    accountsAPI.getAccounts({ account_type: 'business' }).then((accounts: any[]) => {
      setHasBusinessAccounts(accounts.length > 0);
    }).catch(() => {});
  }, []);

  const fetchLinkedAccounts = async () => {
    try {
      setLoading(true);
      const accounts = await accountsAPI.getLinkedAccounts();
      setLinkedAccounts(accounts);
    } catch (error) {
      console.error('Error fetching linked accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectBank = async () => {
    try {
      setConnectingBank(true);
      setPlaidError(null);
      plaidCompletedRef.current = false;
      const response = await accountsAPI.createLinkToken();
      if (response?.link_token) {
        setLinkToken(response.link_token);
      }
    } catch (error) {
      console.error('Error creating link token:', error);
      setConnectingBank(false);
    }
  };

  const handleReconnectBank = async (connectionId: number) => {
    try {
      setReauthConnectionId(connectionId);
      plaidCompletedRef.current = false;
      const response = await accountsAPI.createUpdateLinkToken(connectionId);
      if (response?.link_token) {
        setLinkToken(response.link_token);
      }
    } catch (error) {
      console.error('Error creating update link token:', error);
      setReauthConnectionId(null);
    }
  };

  const onSuccess = async (publicToken: string, metadata: any) => {
    plaidCompletedRef.current = true;
    // Re-auth flow — no token exchange needed, just clear the reauth flag and re-sync
    if (reauthConnectionId !== null) {
      try {
        await accountsAPI.completeReauth(reauthConnectionId);
        await fetchLinkedAccounts();
      } catch (error) {
        console.error('Error completing re-auth:', error);
      } finally {
        setReauthConnectionId(null);
        setLinkToken(null);
      }
      return;
    }

    // Normal new-connection flow
    try {
      setPlaidError(null);
      const response = await accountsAPI.exchangeToken({
        public_token: publicToken,
        institution_name: metadata.institution?.name || 'Unknown Bank',
      });

      if (response.accounts?.length > 0) {
        const allAccountIds = response.accounts.map((acc: any) => acc.account_id);
        setPendingConnectionId(response.connection_id);
        setSelectedAccounts(allAccountIds);

        const suggestions = await accountsAPI.getAccountMatchingSuggestions({
          connection_id: response.connection_id,
          selected_accounts: allAccountIds,
        });

        setPlaidHistoryNote(
          typeof suggestions.plaid_history_note === 'string' ? suggestions.plaid_history_note : null
        );
        setLatestTransactionByManualAccount(
          suggestions.latest_transaction_by_manual_account &&
            typeof suggestions.latest_transaction_by_manual_account === 'object'
            ? suggestions.latest_transaction_by_manual_account
            : {}
        );

        if (suggestions.suggestions?.length > 0) {
          setAccountMatchingSuggestions(suggestions.suggestions);
          setShowAccountMatching(true);
        } else {
          await importAccountsWithMappings({});
        }
      }
    } catch (error) {
      console.error('Error connecting bank account:', error);
      setPlaidError(error instanceof Error ? error.message : 'Failed to connect bank account');
    } finally {
      setConnectingBank(false);
      setLinkToken(null);
    }
  };

  const importAccountsWithMappings = async (
    mappings: { [plaidAccountId: string]: AccountMapping },
    transactionImport?: { [plaidAccountId: string]: PlaidTransactionImportMode }
  ) => {
    try {
      setMatchingLoading(true);
      setImportSuccessMessage(null);

      const skipped = new Set(
        Object.entries(mappings)
          .filter(([, v]) => v === 'skip')
          .map(([id]) => id)
      );
      const accountsToImport = selectedAccounts.filter(id => !skipped.has(id));

      if (accountsToImport.length === 0) {
        setShowAccountMatching(false);
        return;
      }

      const cleanMappings: { [id: string]: number | null } = {};
      const accountTypeOverrides: { [id: string]: 'business' } = {};
      for (const [id, val] of Object.entries(mappings)) {
        if (val === 'skip') continue;
        if (val === 'new_business') {
          cleanMappings[id] = null;
          accountTypeOverrides[id] = 'business';
        } else {
          cleanMappings[id] = val as number | null;
        }
      }

      await accountsAPI.importSelectedAccounts({
        connection_id: pendingConnectionId!,
        selected_accounts: accountsToImport,
        account_mappings: cleanMappings,
        account_type_overrides: accountTypeOverrides,
        transaction_import: transactionImport,
      });

      setShowAccountMatching(false);
      setPendingConnectionId(null);
      setSelectedAccounts([]);
      setAccountMatchingSuggestions([]);
      setPlaidHistoryNote(null);
      setLatestTransactionByManualAccount({});
      setImportSuccessMessage(
        `Linked ${accountsToImport.length} account(s). We're importing transactions in the background; this can take up to a minute. Open Transactions or use Sync all if you don't see updates yet.`
      );
      await fetchLinkedAccounts();
    } catch (error) {
      console.error('Error importing accounts:', error);
      alert('Import failed — check the browser console and server logs for details.');
    } finally {
      setMatchingLoading(false);
    }
  };

  const onExit = (err: any, metadata: any) => {
    if (err) {
      const errorCode = err.error_code || 'UNKNOWN_ERROR';
      const errorMessage = err.error_message || 'Plaid Link exited before completing.';
      const institutionName = metadata?.institution?.name ? ` (${metadata.institution.name})` : '';
      const display = `${errorCode}${institutionName}: ${errorMessage}`;
      console.error('Plaid Link exited with error:', { err, metadata });
      setPlaidError(display);
    } else if (!plaidCompletedRef.current) {
      const institutionName = metadata?.institution?.name || 'your institution';
      const exitStatus = metadata?.status || 'unknown';
      const requestId = metadata?.request_id ? ` request_id=${metadata.request_id}` : '';
      const message = `Link closed before completion for ${institutionName} (status: ${exitStatus}).${requestId}`;
      console.warn('Plaid Link exited without explicit error:', { metadata });
      setPlaidError(message);
    }
    setLinkToken(null);
    setConnectingBank(false);
    setReauthConnectionId(null);
    plaidCompletedRef.current = false;
  };

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const handleSyncAccounts = async () => {
    try {
      setSyncing(true);
      await accountsAPI.syncAccounts();
      await fetchLinkedAccounts();
    } catch (error) {
      console.error('Error syncing accounts:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnectAccount = async (account: LinkedAccount) => {
    const confirmed = window.confirm(
      `Disconnect ${account.account_name}?\n\n` +
      `Your account and transaction history will be kept as manual records in Wealthini — ` +
      `only the live bank sync will stop. You can reconnect at any time.`
    );
    if (!confirmed) return;
    try {
      await accountsAPI.disconnectAccount(account.id);
      await fetchLinkedAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user?.plaid_enabled) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
        <p className="text-4xl">🔒</p>
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Bank sync is a premium feature</h3>
        <p className="text-sm text-[var(--text-muted)] max-w-sm">
          Automatically sync balances and import transactions from your bank. Contact us to enable this feature on your account.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {linkedAccounts.length > 0 && (
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleSyncAccounts} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync all'}
          </Button>
          <Button variant="default" onClick={handleConnectBank} disabled={connectingBank}>
            {connectingBank ? 'Connecting...' : 'Connect a bank'}
          </Button>
        </div>
      )}

      {importSuccessMessage && (
        <div className="rounded-md border border-[var(--success-color)] bg-[var(--background-card)] px-4 py-3 text-sm text-[var(--success-color)]">
          {importSuccessMessage}
        </div>
      )}

      {plaidError && (
        <div className="rounded-md border border-[var(--danger-color)] bg-[var(--background-card)] px-4 py-3 text-sm text-[var(--danger-color)]">
          Plaid connection failed: {plaidError}
        </div>
      )}

      {/* Account list */}
      {linkedAccounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
          <p className="text-4xl">🏦</p>
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">No bank connections yet</h3>
          <p className="text-sm text-[var(--text-muted)] max-w-sm">
            Connect your bank to automatically track balances and import transactions.
          </p>
          <Button variant="default" onClick={handleConnectBank} disabled={connectingBank}>
            Connect a bank
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {linkedAccounts.map(account => (
            <div
              key={account.id}
              className="rounded-xl border border-[var(--border-color)] bg-[var(--background-card)] p-4 flex items-center gap-4"
            >
              <InstitutionLogo
                institution={account.institution_name}
                plaidLogoUrl={account.logo_url}
                size={36}
              />

              <div className="flex-1 min-w-0">
                <p className="font-medium text-[var(--text-primary)] truncate">{account.account_name}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {account.institution_name}
                  {account.mask ? ` · ···· ${account.mask}` : ''}
                  {' · '}
                  <span className="capitalize">{account.account_subtype}</span>
                </p>
                {account.manual_account_id ? (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    Linked to <span className="text-[var(--text-secondary)]">{account.manual_account_name}</span>
                  </p>
                ) : (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">Standalone account</p>
                )}
              </div>

              <div className="text-right shrink-0">
                <p className="font-semibold text-[var(--text-primary)]">
                  {formatCurrency(account.current_balance)}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  Synced {account.last_sync_at ? formatDate(account.last_sync_at) : 'never'}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {account.needs_reauth && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReconnectBank(account.plaid_connection_id)}
                    disabled={reauthConnectionId === account.plaid_connection_id}
                    className="text-[var(--danger-color)] border-[var(--danger-color)] hover:bg-[var(--danger-color)]/10"
                  >
                    {reauthConnectionId === account.plaid_connection_id ? 'Reconnecting...' : 'Reconnect'}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnectAccount(account)}
                  className="text-[var(--text-muted)] hover:text-[var(--danger-color)]"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AccountMatchingModal
        isOpen={showAccountMatching}
        onClose={() => {
          setShowAccountMatching(false);
          setAccountMatchingSuggestions([]);
          setPlaidHistoryNote(null);
          setLatestTransactionByManualAccount({});
        }}
        suggestions={accountMatchingSuggestions}
        onConfirm={(m, t) => importAccountsWithMappings(m, t)}
        loading={matchingLoading}
        hasBusinessAccounts={hasBusinessAccounts}
        plaidHistoryNote={plaidHistoryNote}
        latestTransactionByManualAccount={latestTransactionByManualAccount}
      />
    </div>
  );
};

export default BankConnections;
